import { useState, useEffect } from "react";
import { updateSubscriptionTemplate } from "../services/subTemplateService";
import { getAllAnchors } from "../services/anchorService";
import { fetchWATemplates } from "../services/watemplateService";
import { fetchSubscriptions } from "../services/subscriptionService";

const SubscriptionDetail = ({ subscription, onBack }) => {
  const [formData, setFormData] = useState({
    day: subscription.day || "",
    time: subscription.time?.slice(0, 5) || "",
    anchor: subscription.anchor || "",
    subscription: subscription.subscription || "",
    watemplate: subscription.watemplate || "",
    active: subscription.active?.toString() || "true",
  });

  const [anchors, setAnchors] = useState([]);
  const [waTemplates, setWATemplates] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");



  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [anchorList, templateList, subscriptionList] = await Promise.all([
          getAllAnchors(),
          fetchWATemplates(),
          fetchSubscriptions(),
        ]);
        setAnchors(anchorList);
        setWATemplates(templateList);
        setSubscriptions(subscriptionList);
      } catch (error) {
        setErrorMsg("Failed to load anchors, templates, or subscriptions.");
      }
    };

    fetchInitialData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSuccessMsg("");
    setErrorMsg("");

    if (formData.day === "" || isNaN(formData.day) || Number(formData.day) < 0) {
      setErrorMsg("Day is required and must be 0 or greater.");
      return;
    }
    if (!formData.time) {
      setErrorMsg("Time is required.");
      return;
    }
    if (!formData.anchor) {
      setErrorMsg("Anchor selection is required.");
      return;
    }
    if (!formData.watemplate) {
      setErrorMsg("Template selection is required.");
      return;
    }
    if (!formData.subscription) {
      setErrorMsg("Subscription selection is required.");
      return;
    }


    setLoading(true);
    try {
      await updateSubscriptionTemplate(subscription.id, {
        ...formData,
        time: formData.time + ":00",
      });
      setSuccessMsg("Subscription updated successfully.");
    } catch (error) {
      setErrorMsg("Failed to update subscription.");
    } finally {
      setLoading(false);
    }
  };

  const formatTimeLabel = (timeStr) => {
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0);
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  return (
    <div className="p-4">
      <button
        onClick={onBack}
        className="mb-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
      >
        ← Back
      </button>

      <h2 className="text-2xl font-semibold mb-4">Edit Subscription</h2>

      {successMsg && <p className="text-green-600 mb-4">{successMsg}</p>}
      {errorMsg && <p className="text-red-600 mb-4">{errorMsg}</p>}

      <div className="space-y-4">
        <InputField label="Day" name="day" type="number" value={formData.day} onChange={handleChange} />

        <SelectField
          label="Active"
          name="active"
          value={formData.active}
          onChange={handleChange}
          options={[
            { id: "true", label: "true" },
            { id: "false", label: "false" }
          ]}
        />

        <SelectField
          label="Time"
          name="time"
          value={formData.time}
          onChange={handleChange}
          options={[
            { id: "10:00", label: formatTimeLabel("10:00") },
            { id: "13:00", label: formatTimeLabel("13:00") },
            { id: "16:00", label: formatTimeLabel("16:00") },
          ]}
        />


        {/* Anchor Dropdown */}
        <div>
          <label className="block font-medium mb-1">Anchor:</label>
          <select
            name="anchor"
            value={formData.anchor}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full"
          >
            <option value="">Select Anchor</option>
            {anchors.map((anchor) => (
              <option key={anchor.id} value={anchor.id}>
                {anchor.name} ({anchor.code})
              </option>
            ))}
          </select>
        </div>

        {/* WA Template Dropdown */}
        <div>
          <label className="block font-medium mb-1">WA Template:</label>
          <select
            name="watemplate"
            value={formData.watemplate}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full"
          >
            <option value="">Select Template</option>
            {waTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        {/* Subscription Dropdown */}
        <div>
          <label className="block font-medium mb-1">Subscription Plan:</label>
          <select
            name="subscription"
            value={formData.subscription}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full"
          >
            <option value="">Select Subscription</option>
            {subscriptions.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.name} ({sub.code})
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleSave}
          disabled={loading}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

const InputField = ({ label, name, value, onChange, type = "text" }) => (
  <div>
    <label className="block font-medium mb-1">{label}:</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="border rounded px-3 py-2 w-full"
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options }) => {

  const formatTimeLabel = (timeStr) => {
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0);
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };
  // Ensure selected time is included only once
  const uniqueOptionsMap = new Map();
  options.forEach((item) => uniqueOptionsMap.set(item.id, item));
  if (value && !uniqueOptionsMap.has(value)) {
    uniqueOptionsMap.set(value, { id: value, label: formatTimeLabel(value) });
  }
  const uniqueOptions = Array.from(uniqueOptionsMap.values());

  return (
    <div>
      <label className="block font-medium mb-1">{label}:</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="border rounded px-3 py-2 w-full"
      >
        <option value="">{`Select ${label}`}</option>
        {uniqueOptions.map((item) => (
          <option key={item.id} value={item.id}>
            {item.label}
          </option>
        ))}
      </select>
    </div>
  );
};



export default SubscriptionDetail;
